import React, { useRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";

import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { TextEditor } from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { FeatureType } from "src/clients/features-control";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { generateFeatureMetadata } from "src/featureCatalogue/Queries/Create/utils";

export const FeatureFieldInput: React.FC<{
  fieldName: `columns.features.${number}.${string}`;
  label: string;
  dataLoc: string;
  placeholder: string;
  disabled?: boolean;
  prefixIcon?: { icon: IconProp };
  tooltip?: string;
}> = ({
  fieldName,
  label,
  dataLoc,
  placeholder,
  disabled,
  prefixIcon,
  tooltip,
}) => {
  return (
    <div>
      <Label>
        <div className="flex items-center gap-x-1">
          {label}
          {tooltip && (
            <Tooltip body={tooltip}>
              <Icon icon={faInfoCircle} size="2xs" />
            </Tooltip>
          )}
        </div>
      </Label>
      <Controller
        name={fieldName}
        render={({ field, fieldState }) => (
          <>
            <Input
              data-loc={dataLoc}
              disabled={disabled}
              errored={!!fieldState.error?.message}
              placeholder={placeholder}
              prefixIcon={prefixIcon}
              value={field.value ?? ""}
              fullWidth
              onChange={field.onChange}
            />
            {fieldState.error?.message && (
              <ErrorHint dataLoc={`${dataLoc}-error`}>
                {fieldState.error.message}
              </ErrorHint>
            )}
          </>
        )}
      />
    </div>
  );
};

export const ColumnNameDropdown: React.FC<{
  index: number;
  disabled?: boolean;
  columnsOptions: Array<{
    key: string;
    value: string;
    icon: IconProp;
    type: FeatureType | undefined;
  }>;
}> = ({ index, disabled, columnsOptions }) => {
  const form = useFormContext<QueryFormValues>();

  // Subscribe to window_length so generated feature metadata updates when it changes.
  const windowLength = form.watch("window_length");

  return (
    <div>
      <Label>Column Name</Label>
      <Controller
        name={`columns.features.${index}.column_name`}
        render={({ field, fieldState }) => (
          <>
            <Select
              dataLoc={`feature-column-${index}`}
              disabled={disabled}
              errored={!!fieldState.error?.message}
              options={columnsOptions.map((opt) => ({
                key: opt.key,
                value: opt.value,
                icon: opt.icon,
              }))}
              placeholder="Select column"
              value={field.value ?? null}
              onChange={(value) => {
                field.onChange(value);
                const { featureKey, featureName } = generateFeatureMetadata(
                  value,
                  windowLength ?? undefined,
                );
                form.setValue(
                  `columns.features.${index}.feature_key`,
                  featureKey,
                );
                form.setValue(`columns.features.${index}.name`, featureName);
                form.setValue(
                  `columns.features.${index}.feature_type`,
                  columnsOptions.find((opt) => opt.key === value)?.type,
                );
              }}
            />
            {fieldState.error?.message && (
              <ErrorHint dataLoc={`feature-column-${index}-error`}>
                {fieldState.error.message}
              </ErrorHint>
            )}
          </>
        )}
      />
    </div>
  );
};

export const FeatureDescriptionEditor: React.FC<{
  disabled?: boolean;
  index: number;
}> = ({ disabled, index }) => {
  const toolbarRef = useRef<HTMLUListElement>(null);
  return (
    <>
      <Label>Feature description</Label>
      <div className="flex flex-col rounded border border-gray-200 bg-white p-3">
        <Controller
          name={`columns.features.${index}.description`}
          render={({ field }) => (
            <TextEditor
              dataLoc={`feature-description-${index}`}
              placeholder="Placeholder..."
              readOnly={disabled}
              toolbarRef={toolbarRef}
              value={field.value ?? ""}
              onChange={field.onChange}
            />
          )}
        />
        <div className="flex justify-between gap-x-3 pt-3">
          <Toolbar ref={toolbarRef} exclude={["heading"]} />
        </div>
      </div>
    </>
  );
};
